import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DownloadSimple, CircleNotch, CurrencyDollar } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pipeline stages mirrored from the admin dashboard. "all" is appended as the first option.
const STAGES = [
  { v: 'all', l: 'Any stage' },
  { v: 'profile_created', l: 'Profile Created' },
  { v: 'application_sent', l: 'Application Sent to College' },
  { v: 'docs_under_review', l: 'Docs Under Review (College)' },
  { v: 'docs_verified', l: 'Docs Verified (College)' },
  { v: 'enrollment_agreement_sent', l: 'Enrollment Agreement Sent' },
  { v: 'enrollment_agreement_signed', l: 'Enrollment Agreement Signed' },
  { v: 'sabc_in_process', l: 'SABC In Process' },
  { v: 'coe_sent', l: 'COE Sent' },
  { v: 'half_money_received', l: '1/2 Money Received' },
  { v: 'money_received', l: '2/2 Money Received' },
  { v: 'rejected', l: 'Rejected' },
  { v: 'withdrawn', l: 'Withdrawn' },
];

const money = (n) => (n == null || Number.isNaN(+n)) ? '' : `$${(+n).toLocaleString('en-CA', { maximumFractionDigits: 2 })}`;

export default function SaFinanceTab() {
  const [intakes, setIntakes] = useState([]);
  const [intake, setIntake] = useState('');
  const [stage, setStage] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Row-level edits buffered until blur so we don't fire a PATCH on every keystroke.
  const [draft, setDraft] = useState({}); // { [student_id]: { field: value } }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/sa-finance/intakes`);
        setIntakes(data || []);
        if (data?.length) setIntake(data[0]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load intakes');
      }
    })();
  }, []);

  const refresh = useCallback(async () => {
    if (!intake) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sa-finance/rows`, { params: { intake, stage } });
      setRows(data || []);
      setDraft({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load rows');
    } finally { setLoading(false); }
  }, [intake, stage]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = useMemo(() => {
    const base = { contract: 0, sa: 0, agent: 0 };
    for (const r of rows) {
      base.contract += +r.contract_value || 0;
      base.sa += +r.sa_total_earning || 0;
      base.agent += (+r.agent_first_earning || 0) + (+r.agent_second_earning || 0);
    }
    return base;
  }, [rows]);

  const commitField = async (studentId, field, raw) => {
    const value = field === 'sa_school_value' ? (raw === '' ? null : Number(raw)) : raw;
    try {
      await axios.patch(`${API}/admin/sa-finance/students/${studentId}`, { [field]: value });
      // Local optimistic refresh — pull the recomputed row from the backend
      // so the derived columns (invoice splits, SA earnings) stay honest.
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDownload = async () => {
    if (!intake) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/sa-finance/export.xlsx`, {
        params: { intake, stage },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SA_Finance_${intake.replace(/\s+/g, '_')}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally { setDownloading(false); }
  };

  const setDraftValue = (sid, field, value) => {
    setDraft(d => ({ ...d, [sid]: { ...(d[sid] || {}), [field]: value } }));
  };
  const draftValue = (sid, field, fallback) => (draft[sid]?.[field] ?? fallback);

  return (
    <div className="space-y-5" data-testid="sa-finance-tab">
      <div className="flex items-center gap-2">
        <CurrencyDollar size={20} className="text-emerald-700" />
        <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>SA Finance Report</h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Intake</label>
          <Select value={intake} onValueChange={setIntake}>
            <SelectTrigger data-testid="sa-finance-intake-select"><SelectValue placeholder="Pick an intake" /></SelectTrigger>
            <SelectContent>
              {intakes.map(i => <SelectItem key={i} value={i} data-testid={`sa-finance-intake-${i}`}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Stage</label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid="sa-finance-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STAGES.map(s => <SelectItem key={s.v} value={s.v} data-testid={`sa-finance-stage-${s.v}`}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleDownload} disabled={!intake || downloading} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="sa-finance-download-btn">
          {downloading ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <DownloadSimple size={14} className="mr-1.5" />}
          Download .xlsx
        </Button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatPill label="Rows" value={rows.length} data-testid="sa-finance-stat-rows" />
        <StatPill label="Contract Total" value={money(totals.contract)} data-testid="sa-finance-stat-contract" />
        <StatPill label="Agent Earnings" value={money(totals.agent)} data-testid="sa-finance-stat-agent" />
        <StatPill label="SA Earnings" value={money(totals.sa)} data-testid="sa-finance-stat-sa" />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-xs">
            <thead className="bg-gray-900 text-white sticky top-0">
              <tr>
                {['First', 'Last', 'Date COE Done', 'Date Received Money', 'Amount Rcvd', 'Reg', 'Shared', 'Contract', 'School Val', '1st Inst', '1st Sch', '1st Inv', '2nd Inst', '2nd Sch', '2nd Inv', 'Comment', 'Agent', 'Agent 1st', 'SA 1st', 'Agent 2nd', 'SA 2nd', 'SA Total'].map(h => (
                  <th key={h} className="px-2 py-2 text-left font-semibold text-[11px] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={22} className="text-center text-gray-500 py-8" data-testid="sa-finance-loading">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={22} className="text-center text-gray-400 py-8" data-testid="sa-finance-empty">No students for this filter.</td></tr>
              ) : rows.map(r => (
                <tr key={r.student_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`sa-finance-row-${r.student_id}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.first_name}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.last_name}</td>
                  <td className="px-1 py-1">
                    <Input type="date" value={draftValue(r.student_id, 'sa_coe_done_date', r.date_coe_done)}
                      onChange={e => setDraftValue(r.student_id, 'sa_coe_done_date', e.target.value)}
                      onBlur={e => commitField(r.student_id, 'sa_coe_done_date', e.target.value)}
                      className="h-7 text-xs" data-testid={`sa-coe-${r.student_id}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="date" value={draftValue(r.student_id, 'sa_money_received_date', r.date_received_money)}
                      onChange={e => setDraftValue(r.student_id, 'sa_money_received_date', e.target.value)}
                      onBlur={e => commitField(r.student_id, 'sa_money_received_date', e.target.value)}
                      className="h-7 text-xs" data-testid={`sa-money-${r.student_id}`} />
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.amount_received)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.reg_fee)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.amount_shared)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.contract_value)}</td>
                  <td className="px-1 py-1">
                    <Input type="number" step="100"
                      value={draftValue(r.student_id, 'sa_school_value', r.school_value)}
                      onChange={e => setDraftValue(r.student_id, 'sa_school_value', e.target.value)}
                      onBlur={e => commitField(r.student_id, 'sa_school_value', e.target.value)}
                      className="h-7 text-xs w-24" data-testid={`sa-school-${r.student_id}`} />
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.first_installment)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.first_school_portion)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-semibold text-blue-700">{money(r.first_invoice_value)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.second_installment)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.second_school_portion)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-semibold text-blue-700">{money(r.second_invoice_value)}</td>
                  <td className="px-1 py-1">
                    <Input value={draftValue(r.student_id, 'sa_comment', r.comment)}
                      onChange={e => setDraftValue(r.student_id, 'sa_comment', e.target.value)}
                      onBlur={e => commitField(r.student_id, 'sa_comment', e.target.value)}
                      className="h-7 text-xs w-40" data-testid={`sa-comment-${r.student_id}`} />
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.agent}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.agent_first_earning)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-semibold text-emerald-700">{money(r.sa_first_earning)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{money(r.agent_second_earning)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-semibold text-emerald-700">{money(r.sa_second_earning)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-bold text-emerald-800">{money(r.sa_total_earning)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, 'data-testid': testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={testid}>
      <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}
